// src/app/@shared/services/print/print.service.ts
import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { MapService } from 'src/app/modules/route-planner/services/map/map.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { PREPARING_PRINT_VIEW, CANCEL } from 'src/app/modules/route-planner/constants/multi-language.constants';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  isPrinting: boolean = false;
  private loadingIndicator: HTMLElement | null = null;
  private processStartTime: number = 0; // Track overall process start time
  
  // Performance optimization constants
  // Scale 1.0 provides best balance - lower scales can actually be slower
  // due to browser rendering optimizations and DOM processing overhead
  private readonly CANVAS_SCALE = 1.0;
  private readonly MAX_PARALLEL_CAPTURES = 2; // Process 2 pages in parallel
  private readonly HEIGHT_MEASUREMENT_BATCH_SIZE = 10; // Measure heights in batches
  
  // Helper method to format time in seconds
  private formatTime(ms: number): string {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  
  constructor(private readonly languageService: LanguageService) {}

  /**
   * Captures and prints content with an optional map
   * @param printableElement The element to print
   * @param headerInfo Header information for the print
   * @param mapService Optional map service to capture map
   * @param mapZoomAdjustment Zoom adjustment for map capture
   * @param printSelector Optional CSS selector to find printable content within the component
   * @param resultsPerPage Number of results to display per page (default: 4)
   */
  /**
   * Shows the loading indicator immediately
   * Can be called before captureAndPrint to show loader instantly
   */
  showLoadingIndicator(): void {
    if (this.loadingIndicator && document.body.contains(this.loadingIndicator)) {
      return; // Already showing
    }
    this.isPrinting = true;
    this.loadingIndicator = this.createLoadingIndicator();
    document.body.appendChild(this.loadingIndicator);
    // Force browser to render the loader immediately
    // Use requestAnimationFrame to ensure the loader is painted before any blocking operations
    requestAnimationFrame(() => {
      // Force a reflow to ensure the element is painted
      void this.loadingIndicator!.offsetHeight;
    });
  }

  captureAndPrint(
    printableElement: HTMLElement,
    headerInfo: {
      title?: string;
      details?: { label: string; value: string }[];
      dateTime?: { date?: string; time?: string };
    },
    mapService?: MapService,
    mapZoomAdjustment: number = 2,
    printSelector?: string,
    resultsPerPage: number = 4
  ): void {
    this.processStartTime = performance.now();

    // Show loading indicator if not already shown if not already shown
    if (!this.loadingIndicator || !document.body.contains(this.loadingIndicator)) {
      this.isPrinting = true;
      this.loadingIndicator = this.createLoadingIndicator();
      document.body.appendChild(this.loadingIndicator);
    }

    // If a specific selector is provided, try to find that element first
    if (printSelector) {
      const specificElement = document.querySelector(
        printSelector
      ) as HTMLElement;
      if (specificElement) {
        printableElement = specificElement;
      }
    }

    // Clone and prepare content
    const cloneStartTime = performance.now();
    const contentClone = printableElement.cloneNode(true) as HTMLElement;
    this.removeInteractiveElements(contentClone);

    // Check if this is a source-destination results component
    const hasSourceDestListItems =
      contentClone.querySelector('app-source-destination-results') !== null;

    // Only adjust results per page if not explicitly set for source-destination results
    if (!hasSourceDestListItems || resultsPerPage === 4) {
      // Detect different component types and adjust results per page
      const hasDailyCalendar =
        contentClone.querySelector('app-search-line-daily-calendar') !== null;
      const hasSearchStationCalendar =
        contentClone.querySelector(
          'app-search-station-deatiled-result-routes-calendar'
        ) !== null;
      const hasSourceDestRoute =
        contentClone.querySelector(
          'app-source-destination-detailed-result-route'
        ) !== null;
      const hasSearchLineResults =
        contentClone.querySelector('app-search-line-results') !== null;
      const hasSearchLineDetailedStops =
        contentClone.querySelector('app-search-line-detailed-stops') !== null;
      const hasSearchStations =
        contentClone.querySelector('app-search-stations-result-list-item') !==
        null;
      const hasTable = contentClone.querySelector('table, mot-table') !== null;
      const hasSearchStationsResults =
        contentClone.querySelector(
          'app-search-station-deatiled-result-details'
        ) !== null;
      const hasSearchStationDetailedResultRoutesCalendarList =
        contentClone.querySelector(
          'app-search-station-detailed-result-routes-calendar-list'
        ) !== null;
      // Adjust results per page based on content type
      if (hasDailyCalendar) {
        // Daily calendar tables can fit more items per page
        resultsPerPage = 15;
      } else if (hasSearchStationCalendar) {
        // Search station calendar - increase to fit more on a page
        resultsPerPage = 20;
     
      } else if (hasSearchStationDetailedResultRoutesCalendarList) {
        // Search station detailed result routes calendar list - increase to fit more on a page
        resultsPerPage = 20;
       
      } else if (hasSourceDestRoute) {
        // For source-destination detailed results, always expand all tiles
        this.expandAllTiles(contentClone);

        // Check for very tall expanded content
        const hasTallExpansions = this.measureExpandedSections(contentClone);

        // Since all tiles will be expanded, use a lower number of results per page
        // If there are tall expansions, use even fewer results per page
        resultsPerPage = hasTallExpansions ? 1 : 2;
      
      } else if (hasSearchLineDetailedStops) {
        // For search line detailed stops, use a smaller number to prevent cutoff
        resultsPerPage = 10;
     
      } else if (hasSearchLineResults || hasSearchStations) {
        // These typically have larger items
        resultsPerPage = 8;
      } else if (hasTable) {
        // General table content
        resultsPerPage = 15;
      } else if (hasSearchStationsResults) {
        // Search stations results
        resultsPerPage = 14;
      } else if (resultsPerPage < 8) {
        // Default minimum of 8 for any other content
        resultsPerPage = 4;
      }
    }


    // Find result items to paginate
    const findItemsStartTime = performance.now();
    const resultItems = this.findResultItems(contentClone);

    // If we have more items than should fit on a page, we'll need pagination
    if (resultItems && resultItems.length > resultsPerPage) {
      this.processPaginatedResults(
        resultItems,
        contentClone,
        headerInfo,
        resultsPerPage,
        mapService,
        mapZoomAdjustment
      );
    } else {
      // Process normally (single page)
      const singlePageStartTime = performance.now();
      
      // Create print container with header
      const printContainer = this.createPrintContainer(headerInfo);
      printContainer.appendChild(contentClone);

      // Add to document temporarily
      document.body.appendChild(printContainer);
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';

      // Handle map and finalize
      this.handleMapAndFinalize(
        printContainer,
        this.loadingIndicator!,
        mapService,
        mapZoomAdjustment
      );
    }
  }

  /**
   * Find result items that need to be paginated
   */
  private findResultItems(contentElement: HTMLElement): HTMLElement[] {
    // First check for source-destination results list items directly
    const sourceDestListItems = Array.from(
      contentElement.querySelectorAll(
        'app-source-destination-results-list-item'
      )
    );
    if (sourceDestListItems.length > 0) {
    
      return sourceDestListItems as HTMLElement[];
    }

    // Special handling for search station calendar results to improve performance
    const stationCalendar = contentElement.querySelector(
      'app-search-station-deatiled-result-routes-calendar'
    );
    if (stationCalendar) {

      // Find the calendar list that contains the actual route entries
      const calendarList = stationCalendar.querySelector(
        'app-search-station-detailed-result-routes-calendar-list'
      );

      if (calendarList) {
        // Look for route entries - these are the items we want to paginate
        const routeEntries = Array.from(
          calendarList.querySelectorAll(
            '.route-entry, .calendar-item, mot-list-item, .mat-list-item'
          )
        );

        if (routeEntries.length > 0) {
       
          return routeEntries as HTMLElement[];
        }

        // If no direct route entries found, try to find the closest container
        const listContainer = calendarList.querySelector(
          '.list-container, mat-list, ul, .routes-list'
        );
        if (listContainer) {
          const listItems = Array.from(listContainer.children);
          if (listItems.length > 0) {
           
            return listItems as HTMLElement[];
          }
        }
      }
    }

    // Special handling for search-line-detailed-stops (added to fix pagination in search line results)
    const searchLineStops = contentElement.querySelector(
      'app-search-line-detailed-stops'
    );
    if (searchLineStops) {
      const stopItems = Array.from(
        searchLineStops.querySelectorAll('.flex > app-stop-indicator')
      ).map((item) => item.closest('.flex'));

      if (stopItems.length > 0) {
      
        return stopItems as HTMLElement[];
      }
    }

    // Special handling for daily calendar table rows
    const dailyCalendar = contentElement.querySelector(
      'app-search-line-daily-calendar'
    );
    if (dailyCalendar) {
      // First try to find specific table rows
      const tableRows = Array.from(
        dailyCalendar.querySelectorAll('tr.mat-mdc-row, .mat-row')
      );
      if (tableRows.length > 0) {
        return tableRows as HTMLElement[];
      }

      // If that fails, try to find all table cells as a fallback
      const tableCells = Array.from(
        dailyCalendar.querySelectorAll('td.mat-mdc-cell, .mat-cell')
      );
      if (tableCells.length > 0) {
        return tableCells as HTMLElement[];
      }
    }

    // Special handling for source-destination detailed result
    const sourceDestDetailedResult = contentElement.querySelector(
      'app-source-destination-detailed-result-route'
    );
    if (sourceDestDetailedResult) {
      // Find all mat-expansion-panel elements within the route sections
      const routeSections = Array.from(
        sourceDestDetailedResult.querySelectorAll(
          'app-detailed-result-route-pedestrian, app-detailed-result-route-transit, app-detailed-result-route-vehicle, app-detailed-result-route-destination'
        )
      );
      if (routeSections.length > 0) {
        return routeSections as HTMLElement[];
      }

      // If no expansion panels found, try to find the section components directly
      const sectionComponents = Array.from(
        sourceDestDetailedResult.querySelectorAll(
          'app-detailed-result-route-pedestrian, app-detailed-result-route-transit, app-detailed-result-route-vehicle, app-detailed-result-route-destination'
        )
      );
      if (sectionComponents.length > 0) {
        return sectionComponents as HTMLElement[];
      }

      // If no specific sections found, try to find all direct children that should be paginated
      const directChildren = Array.from(
        sourceDestDetailedResult.children
      ).filter((child) => {
        // Filter out elements that shouldn't be paginated
        const shouldExclude =
          child.classList.contains('header') ||
          child.classList.contains('footer') ||
          child.classList.contains('controls') ||
          child.classList.contains('map-container') ||
          !child.textContent.trim();
        return !shouldExclude;
      });
      if (directChildren.length > 0) {
        return directChildren as HTMLElement[];
      }
    }

    // Look for common result item selectors in the cloned content
    const resultItemSelectors = [
      'app-lines-between-cities-result-list-item',
      'app-source-destination-results-list-item',
      'app-source-destination-detailed-results',
      'app-source-destination-detailed-result-route',
      'app-search-stations-result-list-item',
      'app-search-line-results',
      'app-search-line-detailed-result .flex', // Added to improve search-line results detection
      'app-search-line-detailed-stops .flex', // Added to improve search-line results detection
      // 'app-search-station-deatiled-result-routes-calendar',
      // 'app-search-station-detailed-result-routes-calendar-list',
      'app-search-station-detailed-result-routes-calendar-list-item',
      'app-search-line-results-container',
      'app-search-line-daily-calendar',
      '.results-list-item',
      '.result-item',
      '.route-section',
      '.route-step',
      '.section-item',
      'app-trip-calendar-item',
      '.mat-mdc-list-item',
      'div[role="listitem"]',
      '.mat-row',
      '.mat-mdc-row',
    ];

    // Try each selector until we find results
    for (const selector of resultItemSelectors) {
      const items = Array.from(
        contentElement.querySelectorAll(selector)
      ) as HTMLElement[];
      if (items.length > 0) {
     
        return items;
      }
    }

    // If no specific selector matched, try to identify result items by structure
    const possibleContainers = [
      Array.from(contentElement.querySelectorAll('.results div > div')),
      Array.from(contentElement.querySelectorAll('.results-content > *')),
      Array.from(contentElement.querySelectorAll('.flex-col > div')),
      Array.from(contentElement.querySelectorAll('.p-4 > div')),
      Array.from(
        contentElement.querySelectorAll('.overflow-y-auto > div > div')
      ),
      Array.from(contentElement.querySelectorAll('mot-table tr')),
      Array.from(contentElement.querySelectorAll('.mat-mdc-row')),
      Array.from(contentElement.querySelectorAll('li')),
      Array.from(contentElement.querySelectorAll('.mat-list-item')),
      Array.from(contentElement.querySelectorAll('.list-item')),
    ];

    for (const container of possibleContainers) {
      if (container.length > 1) {
        return container as HTMLElement[];
      }
    }

    return [];
  }

  /**
   * Process results that need to be paginated
   */
  private processPaginatedResults(
    resultItems: HTMLElement[],
    contentClone: HTMLElement,
    headerInfo: {
      title?: string;
      details?: { label: string; value: string }[];
      dateTime?: { date?: string; time?: string };
    },
    resultsPerPage: number,
    mapService?: MapService,
    mapZoomAdjustment: number = 2
  ): void {
    // Find the parent container of the results
    if (resultItems.length === 0) {
      // No results to paginate, process normally
      const printContainer = this.createPrintContainer(headerInfo);
      printContainer.appendChild(contentClone);
      document.body.appendChild(printContainer);
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';
      this.handleMapAndFinalize(
        printContainer,
        this.loadingIndicator!,
        mapService,
        mapZoomAdjustment
      );
      return;
    }

    // Check if these are table rows from a daily calendar
    const isTableRows = resultItems[0].tagName === 'TR';

    // Handle table rows differently than regular list items
    if (isTableRows) {
      this.processPaginatedTableRows(
        resultItems as HTMLTableRowElement[],
        contentClone,
        headerInfo,
        resultsPerPage,
        mapService,
        mapZoomAdjustment
      );
      return;
    }

    const resultsParent = resultItems[0].parentElement;
    if (!resultsParent) {
      // Fallback to normal processing if we can't find a parent
      const printContainer = this.createPrintContainer(headerInfo);
      printContainer.appendChild(contentClone);
      document.body.appendChild(printContainer);
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';
      this.handleMapAndFinalize(
        printContainer,
        this.loadingIndicator!,
        mapService,
        mapZoomAdjustment
      );
      return;
    }

    // Calculate how many pages we need
    const totalPages = Math.ceil(resultItems.length / resultsPerPage);

    // Make a clean copy of all items for processing
    const allItems = Array.from(resultItems).map((item) =>
      item.cloneNode(true)
    ) as HTMLElement[];

    // Optimized batch measurement of item heights
    const itemHeights: number[] = [];
    const estimatedPageHeight = 1000; // Approximate height of an A4 page in pixels at standard resolution

    const measureStartTime = performance.now();

    // Use optimized batch measurement for better performance
    // Create single measurement container to avoid repeated DOM operations
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '1000px'; // Match print container width
    tempDiv.style.visibility = 'hidden';
    document.body.appendChild(tempDiv);

    // Measure all items in a single batch operation
    allItems.forEach((item, index) => {
      const itemClone = item.cloneNode(true) as HTMLElement;
      tempDiv.innerHTML = '';
      tempDiv.appendChild(itemClone);
      itemHeights.push(tempDiv.offsetHeight);
      
      // Log progress for large lists
      if (allItems.length > 20 && (index + 1) % 10 === 0) {
      }
    });

    document.body.removeChild(tempDiv);

    // Prepare to collect all page contents
    const pageContainers: HTMLElement[] = [];

    // Create dynamic pagination based on actual content height
    let currentPageItems: HTMLElement[] = [];
    let currentPageHeight = 0;
    let pageNum = 0;

    allItems.forEach((item, index) => {
      const itemHeight = itemHeights[index];

      // If this item would make the page too tall, or if it's very tall by itself
      if (
        (currentPageHeight + itemHeight > estimatedPageHeight &&
          currentPageItems.length > 0) ||
        itemHeight > estimatedPageHeight * 0.9
      ) {
        // Create a page with current items if we have any
        if (currentPageItems.length > 0) {
          this.createContentPage(
            currentPageItems,
            contentClone,
            resultsParent,
            headerInfo,
            pageNum,
            Math.ceil(allItems.length / resultsPerPage),
            pageContainers
          );
          pageNum++;
          currentPageItems = [];
          currentPageHeight = 0;
        }

        // For very large items that exceed page height, consider splitting them
        if (itemHeight > estimatedPageHeight * 0.9) {
        

          // Just put this large item on its own page
          currentPageItems = [item];
          this.createContentPage(
            currentPageItems,
            contentClone,
            resultsParent,
            headerInfo,
            pageNum,
            Math.ceil(allItems.length / resultsPerPage),
            pageContainers
          );
          pageNum++;
          currentPageItems = [];
          currentPageHeight = 0;
        } else {
          // Regular item, add to new page
          currentPageItems.push(item);
          currentPageHeight += itemHeight;
        }
      } else {
        // Add item to current page
        currentPageItems.push(item);
        currentPageHeight += itemHeight;
      }
    });

    // Create the last page if there are remaining items
    if (currentPageItems.length > 0) {
      this.createContentPage(
        currentPageItems,
        contentClone,
        resultsParent,
        headerInfo,
        pageNum,
        Math.ceil(allItems.length / resultsPerPage),
        pageContainers
      );
    }

    // Process all pages, map will be added as the last page
    this.processMultiplePages(
      pageContainers,
      this.loadingIndicator!,
      mapService,
      mapZoomAdjustment
    );
  }

  /**
   * Helper method to create a content page with the given items
   */
  private createContentPage(
    items: HTMLElement[],
    contentClone: HTMLElement,
    resultsParent: Element,
    headerInfo: {
      title?: string;
      details?: { label: string; value: string }[];
      dateTime?: { date?: string; time?: string };
    },
    pageNum: number,
    totalPages: number,
    pageContainers: HTMLElement[]
  ): void {
    // Create the base content clone
    const pageClone = contentClone.cloneNode(true) as HTMLElement;

    // Find the results container in this clone
    const pageResultsParent = this.findCorrespondingElement(
      resultsParent,
      pageClone
    );

    if (pageResultsParent) {
      // Clear the results parent in this clone
      while (pageResultsParent.firstChild) {
        pageResultsParent.removeChild(pageResultsParent.firstChild);
      }

      // Add the items for this page
      items.forEach((item) => {
        const itemClone = item.cloneNode(true) as HTMLElement;
        pageResultsParent.appendChild(itemClone);
      });

      // Create the print container for this page
      const pageHeaderInfo = { ...headerInfo };
      if (totalPages > 1) {
        // Add page number to the title if we have multiple pages
        pageHeaderInfo.title = `${pageHeaderInfo.title || 'Details'} (${
          pageNum + 1
        }/${totalPages})`;
      }

      const printContainer = this.createPrintContainer(pageHeaderInfo);
      printContainer.appendChild(pageClone);

      pageContainers.push(printContainer);
    }
  }

  /**
   * Measures the expanded sections to determine if there are any particularly tall ones
   * @returns True if there are sections taller than a threshold
   */
  private measureExpandedSections(contentElement: HTMLElement): boolean {
    const sourceDestDetailedResult = contentElement.querySelector(
      'app-source-destination-detailed-result-route'
    );

    if (!sourceDestDetailedResult) return false;

    // Create a temporary div to measure heights
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '1000px'; // Match print container width
    document.body.appendChild(tempDiv);

    let hasTallSections = false;
    const heightThreshold = 500; // Pixel threshold to consider a section "tall"

    // Measure route sections
    const routeSections = sourceDestDetailedResult.querySelectorAll(
      'app-detailed-result-route-pedestrian, app-detailed-result-route-transit, app-detailed-result-route-vehicle, app-detailed-result-route-destination'
    );

    routeSections.forEach((section) => {
      tempDiv.innerHTML = '';
      tempDiv.appendChild(section.cloneNode(true) as HTMLElement);
      const sectionHeight = tempDiv.offsetHeight;

      if (sectionHeight > heightThreshold) {
        hasTallSections = true;
      }
    });

    document.body.removeChild(tempDiv);
    return hasTallSections;
  }

  /**
   * Process paginated table rows specifically for daily calendar
   */
  private processPaginatedTableRows(
    tableRows: HTMLTableRowElement[],
    contentClone: HTMLElement,
    headerInfo: {
      title?: string;
      details?: { label: string; value: string }[];
      dateTime?: { date?: string; time?: string };
    },
    resultsPerPage: number,
    mapService?: MapService,
    mapZoomAdjustment: number = 2
  ): void {
    // Find the table element
    const tableElement =
      contentClone.querySelector('table') ||
      contentClone.querySelector('mot-table');

   

    if (!tableElement) {
      console.error('No table element found for pagination');
      // Fallback to normal processing if we can't find the table
      const printContainer = this.createPrintContainer(headerInfo);
      printContainer.appendChild(contentClone);
      document.body.appendChild(printContainer);
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';
      this.handleMapAndFinalize(
        printContainer,
        this.loadingIndicator!,
        mapService,
        mapZoomAdjustment
      );
      return;
    }

    // Calculate how many pages we need
    const totalPages = Math.ceil(tableRows.length / resultsPerPage);

    // Make a clean copy of all rows for processing
    const allRows = Array.from(tableRows).map((row) =>
      row.cloneNode(true)
    ) as HTMLTableRowElement[];

    // Prepare to collect all page contents
    const pageContainers: HTMLElement[] = [];

    // Get the table header row if it exists
    const headerRow = contentClone.querySelector(
      'table thead tr, mat-header-row, tr.mat-mdc-header-row'
    );


    // Create each page - one operation per page to avoid DOM manipulation issues
    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      // Create the base content clone without table content
      const pageClone = contentClone.cloneNode(true) as HTMLElement;

      // Find the table in this clone
      const pageTable = pageClone.querySelector('table, mot-table');

      if (pageTable) {
        // Add special class to ensure table is visible in print
        pageTable.classList.add('print-visible');
        pageTable.setAttribute(
          'style',
          'display: block !important; visibility: visible !important; overflow: visible !important; height: auto !important; min-height: auto !important;'
        );

        // Clear the table body
        const tableBody = pageTable.querySelector('tbody');
        if (tableBody) {
          tableBody.innerHTML = '';

          // Ensure tbody is visible
          tableBody.setAttribute(
            'style',
            'display: table-row-group !important; visibility: visible !important;'
          );
        } else {
          // If no tbody, clear the table content except header
          const existingRows = pageTable.querySelectorAll(
            'tr:not(.mat-mdc-header-row)'
          );
          existingRows.forEach((row) => row.remove());
        }

        // Calculate the start and end indices for this page
        const start = pageNum * resultsPerPage;
        const end = Math.min(start + resultsPerPage, allRows.length);

        // Add only the rows for this page
        for (let i = start; i < end; i++) {
          const rowClone = allRows[i].cloneNode(true) as HTMLTableRowElement;

          // Ensure row is visible in print
          rowClone.setAttribute(
            'style',
            'display: table-row !important; visibility: visible !important; page-break-inside: avoid !important;'
          );

          // Make sure all cells are visible
          rowClone.querySelectorAll('td, th').forEach((cell) => {
            (cell as HTMLElement).setAttribute(
              'style',
              'display: table-cell !important; visibility: visible !important; overflow: visible !important;'
            );
          });

          if (tableBody) {
            tableBody.appendChild(rowClone);
          } else {
            pageTable.appendChild(rowClone);
          }
        }

        // Create the print container for this page
        const pageHeaderInfo = { ...headerInfo };
        if (totalPages > 1) {
          // Add page number to the title if we have multiple pages
          pageHeaderInfo.title = `${pageHeaderInfo.title || 'Details'} (${
            pageNum + 1
          }/${totalPages})`;
        }

        const printContainer = this.createPrintContainer(pageHeaderInfo);
        printContainer.appendChild(pageClone);

        pageContainers.push(printContainer);
      }
    }

    // Process all pages, map will be added as the last page
    this.processMultiplePages(
      pageContainers,
      this.loadingIndicator!,
      mapService,
      mapZoomAdjustment
    );
  }

  /**
   * Process multiple pages for printing
   */
  private processMultiplePages(
    pageContainers: HTMLElement[],
    loadingIndicator: HTMLElement,
    mapService?: MapService,
    mapZoomAdjustment: number = 2
  ): void {
    const processStartTime = performance.now();

    if (pageContainers.length === 0) {
      // No pages to process
      this.removeLoadingIndicator();
      return;
    }

    // Add all containers to the document but hidden
    const addContainersStartTime = performance.now();
    pageContainers.forEach((container) => {
      document.body.appendChild(container);
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
    });
    // Check if we need to add a map
    if (
      mapService &&
      mapService.map &&
      typeof mapService.captureMap === 'function'
    ) {
      try {
        const mapCaptureStartTime = performance.now();
        
        // Optimized map capture - balanced quality and performance
        mapService
          .captureMap(mapZoomAdjustment, {
            width: 1000, // Reduced from 1200 for better performance
            height: 750, // Reduced from 900 for better performance
            quality: 0.9, // Slightly reduced for better performance (still high quality)
          })
          .then((mapImageUrl) => {
            if (mapImageUrl) {
              // Create a separate container for the map page
              const mapHeaderInfo = {
                title:
                  pageContainers[0]
                    .querySelector('#printHeader h2')
                    ?.textContent?.replace(/\s*\(\d+\/\d+\)$/, '') ||
                  'Map View',
              };
              const mapPageContainer = this.createPrintContainer(mapHeaderInfo);

              // Add map to the map page
              const mapContainer = this.createMapContainer(mapImageUrl);
              mapPageContainer.appendChild(mapContainer);

              // Add the map page to the document
              document.body.appendChild(mapPageContainer);
              mapPageContainer.style.position = 'absolute';
              mapPageContainer.style.left = '-9999px';
              mapPageContainer.style.top = '-9999px';

              // Add the map page as the last page
              pageContainers.push(mapPageContainer);
            }

            // Now capture all pages and create a PDF
            this.captureMultiplePages(pageContainers, loadingIndicator);
          })
          .catch((error) => {
            console.error('[PrintService] ❌ Map capture error:', error);
            this.captureMultiplePages(pageContainers, loadingIndicator);
          });
      } catch (error) {
        console.error('[PrintService] ❌ Error during map capture process:', error);
        this.captureMultiplePages(pageContainers, loadingIndicator);
      }
    } else {
      // No map, just capture the pages
      this.captureMultiplePages(pageContainers, loadingIndicator);
    }
  }

  /**
   * Capture multiple pages and create a PDF
   * Optimized to process pages in parallel (limited) for better performance
   */
  private captureMultiplePages(
    pageContainers: HTMLElement[],
    loadingIndicator: HTMLElement
  ): void {
    // Process pages with limited parallelism for better performance
    this.capturePagesWithParallelism(pageContainers, loadingIndicator);
  }

  /**
   * Capture pages one at a time sequentially
   */
  private capturePageSequentially(
    pageContainers: HTMLElement[],
    currentIndex: number,
    canvases: HTMLCanvasElement[],
    loadingIndicator: HTMLElement
  ): void {
    if (currentIndex >= pageContainers.length) {
      // All pages captured, proceed to pdf creation
      pageContainers.forEach((container) => {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      });
      this.removeLoadingIndicator();
      // Create PDF with all the pages
      const pdf = this.createMultiPagePDF(canvases);

      // Generate filename with date
      const date = new Date();
      const filename = `route-details-${date.getFullYear()}-${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

      // For mobile devices, directly download the PDF
      // For desktop, open the print window
      if (this.isMobileDevice()) {
        pdf.save(`${filename}.pdf`);
      } else {
        // Convert PDF to blob URL and open print window
        const pdfBlob = pdf.output('blob');
        const objectUrl = URL.createObjectURL(pdfBlob);
        this.openPrintWindowForPDF(objectUrl, filename);
      }

      // Clean up any canvas data
      canvases.forEach((canvas) => {
        canvas.width = 0;
        canvas.height = 0;
      });

      return;
    }

    // Capture the current page
    const currentContainer = pageContainers[currentIndex];

    // Check for very tall containers that need special handling
    const containerHeight = currentContainer.scrollHeight;
    const maxSafeHeight = 10000; // Most browsers handle up to ~10,000px in one canvas

    if (containerHeight > maxSafeHeight) {
   
      this.captureLargeContainer(currentContainer, canvases, () => {
        // Continue with next page after this large container is processed
        this.capturePageSequentially(
          pageContainers,
          currentIndex + 1,
          canvases,
          loadingIndicator
        );
      });
      return;
    }

    // Normal capture for standard-sized containers
    // Optimized settings for better performance while maintaining quality
    html2canvas(currentContainer, {
      allowTaint: true,
      useCORS: true,
      scale: this.CANVAS_SCALE, // Optimized scale (max 2) for better performance
      logging: false,
      backgroundColor: '#FFFFFF',
      imageTimeout: 0,
      removeContainer: true,
      foreignObjectRendering: false, // Disable for better performance
      onclone: (clonedDoc) => {
        const clonedContainer = clonedDoc.body.querySelector('div');
        if (clonedContainer) {
          clonedContainer.style.width = '100%';
          clonedContainer.style.backgroundColor = 'white';
          // Ensure text is rendered crisply
          clonedContainer.style.textRendering = 'geometricPrecision';
        }
      },
    })
      .then((canvas) => {
        // Add the captured canvas to our collection
        canvases.push(canvas);

        // Process the next page
        this.capturePageSequentially(
          pageContainers,
          currentIndex + 1,
          canvases,
          loadingIndicator
        );
      })
      .catch((error) => {
        console.error(`Error capturing page ${currentIndex}:`, error);
        // Continue with next page despite error
        this.capturePageSequentially(
          pageContainers,
          currentIndex + 1,
          canvases,
          loadingIndicator
        );
      });
  }

  /**
   * Measure item heights in batches for better performance
   * Reduces DOM operations significantly
   */
  private measureItemHeightsBatch(
    items: HTMLElement[],
    heights: number[]
  ): void {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '1000px'; // Match print container width
    tempDiv.style.visibility = 'hidden';
    document.body.appendChild(tempDiv);

    // Process items in batches to avoid blocking the UI
    const processBatch = (startIndex: number) => {
      const endIndex = Math.min(
        startIndex + this.HEIGHT_MEASUREMENT_BATCH_SIZE,
        items.length
      );

      for (let i = startIndex; i < endIndex; i++) {
        const itemClone = items[i].cloneNode(true) as HTMLElement;
        tempDiv.innerHTML = '';
        tempDiv.appendChild(itemClone);
        heights[i] = tempDiv.offsetHeight;
      }

      if (endIndex < items.length) {
        // Use requestAnimationFrame to yield to browser between batches
        requestAnimationFrame(() => {
          processBatch(endIndex);
        });
      } else {
        // All measurements done, clean up
        document.body.removeChild(tempDiv);
      }
    };

    processBatch(0);
  }

  /**
   * Capture pages with limited parallelism for better performance
   * Processes multiple pages simultaneously (up to MAX_PARALLEL_CAPTURES)
   */
  private capturePagesWithParallelism(
    pageContainers: HTMLElement[],
    loadingIndicator: HTMLElement
  ): void {
    const captureStartTime = performance.now();
    
    const canvases: HTMLCanvasElement[] = [];
    let completedCount = 0;
    let currentIndex = 0;
    let activeCaptures = 0;
    const pageTimings: { [key: number]: { start: number; end?: number } } = {};

    const processNext = () => {
      // Start new captures if we have capacity and more pages to process
      while (
        activeCaptures < this.MAX_PARALLEL_CAPTURES &&
        currentIndex < pageContainers.length
      ) {
        const index = currentIndex++;
        const container = pageContainers[index];
        activeCaptures++;

        // Check for very tall containers
        const containerHeight = container.scrollHeight;
        const maxSafeHeight = 10000;

        if (containerHeight > maxSafeHeight) {
          // Handle large containers sequentially
          this.captureLargeContainer(container, canvases, () => {
            activeCaptures--;
            completedCount++;
            if (completedCount === pageContainers.length) {
              this.finalizeMultiPagePDF(pageContainers, canvases, loadingIndicator);
            } else {
              processNext();
            }
          });
        } else {
          // Normal capture
          pageTimings[index] = { start: performance.now() };
          
          html2canvas(container, {
            allowTaint: true,
            useCORS: true,
            scale: this.CANVAS_SCALE,
            logging: false,
            backgroundColor: '#FFFFFF',
            imageTimeout: 0,
            removeContainer: true,
            foreignObjectRendering: false,
            // Performance optimizations
            proxy: undefined, // Disable proxy for better performance
            ignoreElements: (element) => {
              // Skip hidden elements to speed up rendering
              const style = window.getComputedStyle(element);
              return style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0';
            },
            onclone: (clonedDoc) => {
              const clonedContainer = clonedDoc.body.querySelector('div');
              if (clonedContainer) {
                clonedContainer.style.width = '100%';
                clonedContainer.style.backgroundColor = 'white';
                clonedContainer.style.textRendering = 'geometricPrecision';
              }
            },
          })
            .then((canvas) => {
              pageTimings[index].end = performance.now();
              const pageDuration = pageTimings[index].end - pageTimings[index].start;
              
              canvases[index] = canvas; // Store in correct position
              activeCaptures--;
              completedCount++;
              
              if (completedCount === pageContainers.length) {
                const totalDuration = performance.now() - captureStartTime;
                this.finalizeMultiPagePDF(pageContainers, canvases, loadingIndicator);
              } else {
                processNext();
              }
            })
            .catch((error) => {
              pageTimings[index].end = performance.now();
              const pageDuration = pageTimings[index].end - pageTimings[index].start;
              console.error(`[PrintService] ❌ Error capturing page ${index + 1}:`, error, {
                duration: `${pageDuration.toFixed(2)}ms`
              });
              activeCaptures--;
              completedCount++;
              if (completedCount === pageContainers.length) {
                const totalDuration = performance.now() - captureStartTime;
                this.finalizeMultiPagePDF(pageContainers, canvases, loadingIndicator);
              } else {
                processNext();
              }
            });
        }
      }
    };

    processNext();
  }

  /**
   * Finalize multi-page PDF creation
   */
  private finalizeMultiPagePDF(
    pageContainers: HTMLElement[],
    canvases: HTMLCanvasElement[],
    loadingIndicator: HTMLElement
  ): void {
    const pdfStartTime = performance.now();
    
    // Clean up containers
    const cleanupStartTime = performance.now();
    pageContainers.forEach((container) => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    });
    
    this.removeLoadingIndicator();
    
    // Filter out undefined entries and create PDF
    const validCanvases = canvases.filter((c) => c !== undefined) as HTMLCanvasElement[];
    const createPdfStartTime = performance.now();
    const pdf = this.createMultiPagePDF(validCanvases);

    // Generate filename with date
    const date = new Date();
    const filename = `route-details-${date.getFullYear()}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

    const saveStartTime = performance.now();
    // For mobile devices, directly download the PDF
    // For desktop, open the print window
    if (this.isMobileDevice()) {
      pdf.save(`${filename}.pdf`);
    } else {
      // Convert PDF to blob URL and open print window
      const blobStartTime = performance.now();
      const pdfBlob = pdf.output('blob');
      const objectUrl = URL.createObjectURL(pdfBlob);
      this.openPrintWindowForPDF(objectUrl, filename);
    }

    // Clean up canvas data
    validCanvases.forEach((canvas) => {
      canvas.width = 0;
      canvas.height = 0;
    });
    
    const totalProcessTime = performance.now() - this.processStartTime;
  }

  /**
   * Capture a large container by splitting it into multiple sections
   */
  private captureLargeContainer(
    container: HTMLElement,
    canvases: HTMLCanvasElement[],
    onComplete: () => void
  ): void {
    // Create a temporary container to hold the content
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = container.style.width || '1000px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.padding = '20px';
    document.body.appendChild(tempContainer);

    // Clone the original container
    const contentClone = container.cloneNode(true) as HTMLElement;

    // Find all significant content sections
    const sections = Array.from(
      contentClone.querySelectorAll(
        'mat-expansion-panel, .mat-expansion-panel, ' +
          'app-detailed-result-route-pedestrian, app-detailed-result-route-transit, ' +
          'app-detailed-result-route-vehicle, app-detailed-result-route-destination, ' +
          '.route-section, .route-step, .section-item, ' +
          '.stations-list, .stops-list, .expanded-content'
      )
    ) as HTMLElement[];

    // If we couldn't find logical sections, fallback to general content elements
    const fallbackSections =
      sections.length > 0
        ? sections
        : (Array.from(
            contentClone.querySelectorAll('div > div')
          ) as HTMLElement[]);

    // Process sections one by one
    this.processSectionsSequentially(
      container,
      fallbackSections,
      0,
      canvases,
      onComplete
    );
  }

  /**
   * Process content sections sequentially
   */
  private processSectionsSequentially(
    originalContainer: HTMLElement,
    sections: HTMLElement[],
    currentIndex: number,
    canvases: HTMLCanvasElement[],
    onComplete: () => void
  ): void {
    if (currentIndex >= sections.length) {
      // All sections processed
      onComplete();
      return;
    }

    // Get the current section
    const currentSection = sections[currentIndex];
    if (!currentSection) {
      // Skip invalid sections
      this.processSectionsSequentially(
        originalContainer,
        sections,
        currentIndex + 1,
        canvases,
        onComplete
      );
      return;
    }

    // Create a new container with header for this section
    const sectionContainer = document.createElement('div');
    sectionContainer.style.width = originalContainer.style.width || '1000px';
    sectionContainer.style.backgroundColor = 'white';
    sectionContainer.style.padding = '20px';

    // Copy the header from the original container
    const originalHeader = originalContainer.querySelector('#printHeader');
    if (originalHeader) {
      const headerClone = originalHeader.cloneNode(true) as HTMLElement;
      sectionContainer.appendChild(headerClone);
    }

    // Add the current section
    const sectionClone = currentSection.cloneNode(true) as HTMLElement;
    sectionContainer.appendChild(sectionClone);

    // Add to document temporarily
    document.body.appendChild(sectionContainer);
    sectionContainer.style.position = 'absolute';
    sectionContainer.style.left = '-9999px';
    sectionContainer.style.top = '-9999px';

    // Capture this section
    html2canvas(sectionContainer, {
      allowTaint: true,
      useCORS: true,
      scale: this.CANVAS_SCALE, // Use optimized scale
      logging: false,
      backgroundColor: '#FFFFFF',
      imageTimeout: 0,
      removeContainer: true,
      foreignObjectRendering: false, // Disable for better performance
    })
      .then((canvas) => {
        // Add the captured canvas to our collection
        canvases.push(canvas);

        // Remove the temporary container
        if (document.body.contains(sectionContainer)) {
          document.body.removeChild(sectionContainer);
        }

        // Process the next section
        this.processSectionsSequentially(
          originalContainer,
          sections,
          currentIndex + 1,
          canvases,
          onComplete
        );
      })
      .catch((error) => {
        console.error(`Error capturing section ${currentIndex}:`, error);

        // Remove the temporary container
        if (document.body.contains(sectionContainer)) {
          document.body.removeChild(sectionContainer);
        }

        // Continue with next section despite error
        this.processSectionsSequentially(
          originalContainer,
          sections,
          currentIndex + 1,
          canvases,
          onComplete
        );
      });
  }

  /**
   * Create a multi-page PDF from canvases
   */
  private createMultiPagePDF(canvases: HTMLCanvasElement[]): jsPDF {
    const pdfStartTime = performance.now();
    
    const pdf = new jsPDF('p', 'mm', 'a4', true);
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 280; // A4 height in mm
    const margin = 10; // Margin in mm
    const usableHeight = pageHeight - margin * 2; // Available height for image content

    // Add each canvas as a separate page
    canvases.forEach((canvas, index) => {
      const pageStartTime = performance.now();
      // Add a new page for all pages except the first
      if (index > 0) {
        pdf.addPage();
      }

      try {
        // Get image data with high quality settings
        let imgData: string;
        if (canvas.width > 2000 || canvas.height > 2000) {
          // Create a scaled version for very large canvases while maintaining quality
          const scaleFactor = Math.min(
            3000 / canvas.width,
            3000 / canvas.height
          );
          const scaledCanvas = document.createElement('canvas');
          scaledCanvas.width = canvas.width * scaleFactor;
          scaledCanvas.height = canvas.height * scaleFactor;
          const ctx = scaledCanvas.getContext('2d', { alpha: false });
          if (ctx) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, scaledCanvas.width, scaledCanvas.height);
            // Enable image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(
              canvas,
              0,
              0,
              scaledCanvas.width,
              scaledCanvas.height
            );
          }
          imgData = scaledCanvas.toDataURL('image/jpeg', 1.0);
        } else {
          // Use original canvas with maximum quality
          imgData = canvas.toDataURL('image/jpeg', 1.0);
        }

        // Calculate the image height when scaled to fit the page width
        const imgHeight = (canvas.height * pageWidth) / canvas.width;

        // Check if the image height exceeds the usable page height
        if (imgHeight > usableHeight) {
          // Image is too tall - need to split across multiple pages
          const scaleFactor = canvas.width / pageWidth; // Calculate px-to-mm conversion factor
          const totalHeightMm = canvas.height / scaleFactor;

          // Calculate how many pages are needed
          const numPages = Math.ceil(totalHeightMm / usableHeight);

          // For each page, show a portion of the image
          for (let page = 0; page < numPages; page++) {
            if (page > 0) {
              pdf.addPage();
            }

            // Calculate which portion of the image to show
            const sourceY = Math.round(page * usableHeight * scaleFactor);
            const sourceHeight = Math.min(
              Math.round(usableHeight * scaleFactor),
              canvas.height - sourceY
            );
            const destHeight = sourceHeight / scaleFactor;

            // Create a temporary canvas with just this section of the image
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = canvas.width;
            tempCanvas.height = sourceHeight;

            const tempCtx = tempCanvas.getContext('2d', { alpha: false });
            if (tempCtx) {
              tempCtx.fillStyle = '#FFFFFF';
              tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

              // Draw just the section we need
              tempCtx.drawImage(
                canvas,
                0,
                sourceY, // Source X, Y
                canvas.width,
                sourceHeight, // Source Width, Height
                0,
                0, // Destination X, Y
                tempCanvas.width,
                tempCanvas.height // Destination Width, Height
              );

              // Get image data for this section
              const sectionImgData = tempCanvas.toDataURL('image/jpeg', 1.0);

              // Add just this section to the PDF
              pdf.addImage(
                sectionImgData,
                'JPEG',
                0,
                margin,
                pageWidth,
                destHeight,
                undefined,
                'FAST'
              );
            }
          }
        } else {
          // Image fits on a single page
          pdf.addImage(
            imgData,
            'JPEG',
            0,
            margin,
            pageWidth,
            imgHeight,
            undefined,
            'FAST'
          );
        }
      } catch (error) {
        console.error(`[PrintService] ❌ Error adding page ${index + 1} to PDF:`, error);
        // Add an error page
        pdf.setFontSize(14);
        pdf.text(`Error adding page ${index + 1}`, 20, 20);
      }
      
      const pageDuration = performance.now() - pageStartTime;
      if (pageDuration > 100) { // Log only if page took more than 100ms
      }
    });

    // Add page numbers to all pages after PDF is fully constructed
    const totalPageCount = pdf.getNumberOfPages();

    // For each page, add page number at the bottom
    for (let i = 1; i <= totalPageCount; i++) {
      pdf.setPage(i);

      // Set font style for page numbers
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      // Calculate center position for page number
      const text = `${i} / ${totalPageCount}`;
      const textWidth =
        (pdf.getStringUnitWidth(text) * 10) / pdf.internal.scaleFactor;
      const textX = (pageWidth - textWidth) / 2;

      // Position at bottom of page with margin
      pdf.text(text, textX, pageHeight - 5);
    }

    return pdf;
  }

  /**
   * Open a print window specifically for PDFs
   */
  private openPrintWindowForPDF(pdfUrl: string, filename: string): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      console.error(
        'Failed to open print window. Please check popup blocker settings.'
      );
      // Fallback to direct download if window can't be opened
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfUrl);
      }, 100);
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${filename}</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              height: 100vh;
              overflow: hidden;
            }
            iframe {
              border: none;
              width: 100%;
              height: 100%;
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
              padding: 10px 20px;
              background-color: #0068F5;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-family: Arial, sans-serif;
              font-size: 14px;
            }
            .print-button:hover {
              background-color: #0056cc;
            }
            @media print {
              .print-button {
                display: none;
              }
            }
          </style>
        </head>
        <body>

          <iframe src="${pdfUrl}" frameborder="0"></iframe>
          <script>
            window.onload = function() {
              // Automatically open print dialog after a short delay to ensure PDF is loaded
              setTimeout(() => {
                // Print is initiated by user via button
                // window.print();
              }, 1000);

              // Clean up when the window is closed
              window.onbeforeunload = function() {
                window.opener && window.opener.URL.revokeObjectURL('${pdfUrl}');
              };
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  }

  /**
   * Find a corresponding element in the cloned document
   */
  private findCorrespondingElement(
    originalElement: Element,
    cloneRoot: HTMLElement
  ): HTMLElement | null {
    // Try to find by id first
    if (originalElement.id) {
      const byId = cloneRoot.querySelector(`#${originalElement.id}`);
      if (byId) return byId as HTMLElement;
    }

    // Try to find by class and tag combination
    const tagName = originalElement.tagName;
    const classNames = Array.from(originalElement.classList).join('.');

    if (classNames) {
      const selector = `${tagName.toLowerCase()}.${classNames}`;
      const byClass = cloneRoot.querySelector(selector);
      if (byClass) return byClass as HTMLElement;
    }

    // Try to find by path - construct a path from the root to the element
    const path = this.buildElementPath(originalElement);
    if (path) {
      const byPath = this.findElementByPath(path, cloneRoot);
      if (byPath) return byPath;
    }

    // Fallback to any similar container
    return cloneRoot.querySelector('.results, .flex-col, .p-4') as HTMLElement;
  }

  /**
   * Build a path from root to the element
   */
  private buildElementPath(element: Element): string[] {
    const path: string[] = [];
    let current: Element | null = element;

    while (current && current.parentElement) {
      const siblings = Array.from(current.parentElement.children);
      const index = siblings.indexOf(current);

      // Create a selector for this element
      let selector = current.tagName.toLowerCase();
      if (current.classList.length > 0) {
        selector += `.${Array.from(current.classList).join('.')}`;
      }
      selector += `:nth-child(${index + 1})`;

      path.unshift(selector);
      current = current.parentElement;
    }

    return path;
  }

  /**
   * Find an element by path in the cloned document
   */
  private findElementByPath(
    path: string[],
    root: HTMLElement
  ): HTMLElement | null {
    let current: HTMLElement = root;

    for (const selector of path) {
      const next = current.querySelector(selector) as HTMLElement;
      if (!next) return null;
      current = next;
    }

    return current;
  }

  /**
   * Handle map and finalize a single page
   */
  private handleMapAndFinalize(
    printContainer: HTMLElement,
    loadingIndicator: HTMLElement,
    mapService?: MapService,
    mapZoomAdjustment: number = 2
  ): void {
    // Check if map service is provided, if the map is initialized, and if it's not null
    if (
      mapService &&
      mapService.map &&
      typeof mapService.captureMap === 'function'
    ) {
      try {
        // Optimized map capture - balanced quality and performance
        mapService
          .captureMap(mapZoomAdjustment, {
            width: 1000, // Reduced from 1200 for better performance
            height: 750, // Reduced from 900 for better performance
            quality: 0.9, // Slightly reduced for better performance (still high quality)
          })
          .then((mapImageUrl) => {
            if (mapImageUrl) {
              // For single page printing, create separate containers and process as multiple pages
              const contentContainer = printContainer.cloneNode(
                true
              ) as HTMLElement;
              const mapHeaderInfo = {
                title:
                  printContainer
                    .querySelector('#printHeader h2')
                    ?.textContent?.replace(/\s*\(\d+\/\d+\)$/, '') ||
                  'Map View',
              };
              const mapPageContainer = this.createPrintContainer(mapHeaderInfo);

              // Add map to its own container
              const mapContainer = this.createMapContainer(mapImageUrl);
              mapPageContainer.appendChild(mapContainer);

              // Process as multiple pages for better handling
              const containers = [contentContainer, mapPageContainer];

              // Add all containers to the document but hidden
              containers.forEach((container) => {
                document.body.appendChild(container);
                container.style.position = 'absolute';
                container.style.left = '-9999px';
                container.style.top = '-9999px';
              });

              // Clean up original container since we're using the clones
              if (document.body.contains(printContainer)) {
                document.body.removeChild(printContainer);
              }

      // Process the multiple pages
      this.captureMultiplePages(containers, this.loadingIndicator!);
            } else {
              // No map image, just finalize the original container
              this.finalizeCapture(printContainer, loadingIndicator);
            }
          })
          .catch((error) => {
            console.error('[PrintService] ❌ Map capture error:', error);
            this.finalizeCapture(printContainer, loadingIndicator);
          });
      } catch (error) {
        console.error('[PrintService] ❌ Error during map capture process:', error);
        this.finalizeCapture(printContainer, loadingIndicator);
      }
    } else {
      // No map service, map not initialized, or captureMap not available
      this.finalizeCapture(printContainer, loadingIndicator);
    }
  }

  private createLoadingIndicator(): HTMLElement {
    const currentLanguage = this.languageService.getSelectedLanguage().language;
    
    // Add spinner animation style only once
    if (!document.getElementById('print-spinner-style')) {
      const style = document.createElement('style');
      style.id = 'print-spinner-style';
      style.textContent = `
        @keyframes printSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Create full-screen overlay
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    overlay.style.zIndex = '99999';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.id = 'print-loading-overlay';
    
    // Create popup container
    const popup = document.createElement('div');
    popup.style.backgroundColor = '#ffffff';
    popup.style.borderRadius = '8px';
    popup.style.padding = '30px';
    popup.style.maxWidth = '400px';
    popup.style.width = '90%';
    popup.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
    popup.style.textAlign = 'center';
    popup.style.position = 'relative';
    
    // Create loading spinner
    const spinner = document.createElement('div');
    spinner.style.width = '50px';
    spinner.style.height = '50px';
    spinner.style.margin = '0 auto 20px';
    spinner.style.border = '4px solid #f3f3f3';
    spinner.style.borderTop = '4px solid #0068F5';
    spinner.style.borderRadius = '50%';
    spinner.style.animation = 'printSpin 1s linear infinite';
    popup.appendChild(spinner);
    
    // Create text content
    const text = document.createElement('div');
    text.style.marginBottom = '30px';
    text.style.fontSize = '16px';
    text.style.color = '#333333';
    text.style.lineHeight = '1.5';
    text.textContent = PREPARING_PRINT_VIEW[currentLanguage];
    popup.appendChild(text);
    
    // Create cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = CANCEL[currentLanguage];
    cancelButton.style.backgroundColor = '#f44336';
    cancelButton.style.color = '#ffffff';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '4px';
    cancelButton.style.padding = '12px 24px';
    cancelButton.style.fontSize = '14px';
    cancelButton.style.cursor = 'pointer';
    cancelButton.style.fontWeight = '500';
    cancelButton.style.transition = 'background-color 0.3s';
    cancelButton.style.marginTop = '10px';
    cancelButton.style.minWidth = '120px';
    
    // Hover effect
    cancelButton.onmouseenter = () => {
      cancelButton.style.backgroundColor = '#d32f2f';
    };
    cancelButton.onmouseleave = () => {
      cancelButton.style.backgroundColor = '#f44336';
    };
    
    // Cancel button click handler - reload page
    cancelButton.onclick = () => {
      window.location.reload();
    };
    
    popup.appendChild(cancelButton);
    overlay.appendChild(popup);
    
    return overlay;
  }

  private removeLoadingIndicator(): void {
    if (this.loadingIndicator && document.body.contains(this.loadingIndicator)) {
      document.body.removeChild(this.loadingIndicator);
    }
    this.loadingIndicator = null;
    this.isPrinting = false;
  }

  private createPrintContainer(headerInfo: {
    title?: string;
    details?: { label: string; value: string }[];
    dateTime?: {
      date?: string;
      time?: string;
      dateLabel?: string;
      timeLabel?: string;
    };
  }): HTMLElement {
    const printContainer = document.createElement('div');
    printContainer.style.width = '1000px';
    printContainer.style.backgroundColor = 'white';
    printContainer.style.padding = '20px';

    // Add print-specific styles
    const printStyles = document.createElement('style');
    printStyles.textContent = `
      @media print {
        /* Table styles */
        mot-table, .mat-mdc-table {
          display: table !important;
          width: 100% !important;
          border-collapse: collapse !important;
        }
        tr.mat-mdc-row, tr.mat-row {
          display: table-row !important;
          page-break-inside: avoid !important;
        }
        td.mat-mdc-cell, th.mat-mdc-header-cell, td.mat-cell, th.mat-header-cell {
          display: table-cell !important;
          padding: 8px !important;
          border: 1px solid #ddd !important;
        }

        /* Route section styles */
        .route-section, .route-step, .section-item {
          display: block !important;
          page-break-inside: avoid !important;
          margin-bottom: 10px !important;
        }

        /* Better control over page breaks */
        mat-expansion-panel, .mat-expansion-panel {
          page-break-inside: avoid !important;
          display: block !important;
          break-inside: avoid !important;
        }

        /* Expansion panel content should stay with its header */
        .mat-expansion-panel-header {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }

        /* Any elements that normally create flex layouts */
        .mat-expansion-panel-content, .mat-expansion-panel-body,
        app-detailed-result-route-pedestrian, app-detailed-result-route-transit,
        app-detailed-result-route-vehicle, app-detailed-result-route-destination {
          display: block !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        /* Stations list should stay together if possible */
        .stations-list, .stops-list, .expanded-content {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        /* Ensure no page breaks between item headers and their content */
        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }

        /* This helps prevent orphaned content */
        p, ul, ol, dl, blockquote {
          orphans: 3;
          widows: 3;
        }

        /* General container styles */
        .print-table, .daily-calendar, app-search-line-daily-calendar mot-table {
          overflow: visible !important;
          height: auto !important;
          min-height: 0 !important;
        }

        /* Make all content visible */
        .print-visible, .mat-mdc-tab-body-active, .mat-tab-body-active,
        app-source-destination-detailed-result-route, app-search-line-daily-calendar {
          display: block !important;
          visibility: visible !important;
          overflow: visible !important;
          height: auto !important;
        }

        /* Hide interactive elements */
        .print-hide, .hide-for-print, .mat-tab-labels, .mat-tab-label, .mat-tab-list,
        button:not(.print-button), .action-button, .control-button,
        .route-planner-controls, .controls, .header-buttons, .footer-buttons,
        .info-panel .close, .close-button, .filter-controls, .filter-panel,
        app-app-bar, .app-bar, .search-controls, .filter-bar, .map-zoom-controls,
        .map-type-controls, .map-controls, .action-bar, .action-panel,
        .header-row button, .header-row-actions, .mobile-view-toggle,
        .view-toggle, app-route-planner-map {
          display: none !important;
        }

        /* Fixed position elements cause problems in print - reset them */
        .fixed, .fixed-position, .sticky, .sticky-position,
        [style*="position: fixed"], [style*="position: sticky"] {
          position: relative !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
        }

        /* Ensure table content isn't truncated */
        td div, th div, td span, th span, td p, th p, td, th {
          max-height: none !important;
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: clip !important;
        }

        /* Ensure mat-expansion-panel is not cut off */
        mat-expansion-panel, .mat-expansion-panel {
          overflow: visible !important;
        }

        /* Ensure all expanded content is fully visible */
        mat-expansion-panel.mat-expanded .mat-expansion-panel-content,
        .mat-expansion-panel.mat-expanded .mat-expansion-panel-content {
          display: block !important;
          visibility: visible !important;
          height: auto !important;
          overflow: visible !important;
        }

        /* Override mat-accordion/panel styling */
        mat-accordion, .mat-accordion {
          display: block !important;
        }

        /* Prevent map-related content from printing if it should be on a separate page */
        .app-map-container {
          display: none !important;
        }

        /* Adjustments for multi-column layouts */
        .column, .col, .flex-col, .grid-col, [class*="col-"] {
          display: block !important;
          width: 100% !important;
          float: none !important;
          padding: 0 !important;
          margin: 0 0 10px 0 !important;
        }

        /* Ensure PDF looks better */
        body, html {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          background: white !important;
          font-size: 13px !important;
          line-height: 1.3 !important;
        }

        /* Allow colors to be printed */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `;
    printContainer.appendChild(printStyles);

    // Create header with grid layout for details
    let headerHtml = `
      <div id="printHeader"  font-size: 16px;"><h2></h2>
    `;

    // Add date and time information if provided
    if (
      headerInfo.dateTime &&
      (headerInfo.dateTime.date || headerInfo.dateTime.time)
    ) {
      headerHtml += `<div class="print-date-time" style="margin-bottom: 10px; font-weight: bold;">`;

      if (headerInfo.dateTime.date) {
        const dateLabel = headerInfo.dateTime.dateLabel || 'Date';
        headerHtml += `<span >${dateLabel}: ${headerInfo.dateTime.date}</span>`;
      }

      if (headerInfo.dateTime.time) {
        const timeLabel = headerInfo.dateTime.timeLabel || 'Time';
        headerHtml += `<span style="margin-right: 15px;">${timeLabel}: ${headerInfo.dateTime.time}</span>`;
      }

      headerHtml += `</div>`;
    }

    // Check if we have details to display
    if (headerInfo.details && headerInfo.details.length > 0) {
      // Identify key travel details vs price details
      const travelDetails = headerInfo.details.filter((detail) => {
        const valueIsString = typeof detail.value === 'string';
        return (
          !detail.label.includes('₪') &&
          !(valueIsString && detail.value.includes('₪'))
        );
      });

      const priceDetails = headerInfo.details.filter((detail) => {
        const valueIsString = typeof detail.value === 'string';
        return (
          detail.label.includes('₪') ||
          (valueIsString && detail.value.includes('₪'))
        );
      });

      // Create grid layout for the travel details
      headerHtml += `<div class="print-header-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">`;

      // Add travel details in a grid layout
      travelDetails.forEach((detail) => {
        headerHtml += `
          <div class="print-header-item" style="margin: 0px 0;">
            <strong>${detail.label}:</strong> ${detail.value}
          </div>
        `;
      });

      headerHtml += `</div>`;

      // Add price details in a horizontal row below if they exist
      if (priceDetails.length > 0) {
        headerHtml += `<div class="print-price-info" style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #eee; display: flex;  flex-wrap: wrap;">`;
        priceDetails.forEach((detail) => {
          headerHtml += `<div class="print-price-item" style="margin-right: 10px; white-space: nowrap;"><strong>${detail.label}:</strong> ${detail.value}</div>`;
        });
        headerHtml += `</div>`;
      }
    }

    headerHtml += `<hr style="margin: 15px 0;"></div>`;

    printContainer.innerHTML += headerHtml;

    return printContainer;
  }

  private removeInteractiveElements(element: HTMLElement): void {
    // Remove all interactive elements
    const interactiveElements = element.querySelectorAll(
      'button, input, select, textarea, [role="button"], [tabindex="0"], app-special-messages-counter, app-tariff-profile-selector'
    );
    interactiveElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.display = 'none';
      }
    });

    // Remove the calendar pick container by ID
    const calendarPickContainer = element.querySelector(
      '#calendar-pick-container, #day-select'
    );
    if (calendarPickContainer && calendarPickContainer instanceof HTMLElement) {
      calendarPickContainer.remove();
    }

    // Remove buttons and interactive elements
    const elementsToRemove = [
      'mot-button',
      'mot-menu',
      'button',
      // '[mat-tab-label]',
      //  'app-loader',
      //  '.loading-indicator',
      'input[type="checkbox"]',
      'input[type="radio"]',
      'a.btn',
      //  '.interactive-control',
      // '[role="button"]',
      //  '.map-controls',
      //  '.controls-container',
      // 'ng-icon',
      'app-rating',
    ];

    // Create a single selector
    const selector = elementsToRemove.join(', ');
    element.querySelectorAll(selector).forEach((el) => {
      el.remove();
    });

    // Special handling for station search calendar results - filter by selected date
    const stationCalendar = element.querySelector(
      'app-search-station-deatiled-result-routes-calendar'
    );
    if (stationCalendar) {
  

      // Find the selected/active day
      const dayPicker = stationCalendar.querySelector('app-day-picker');
      let selectedDay = null;

      if (dayPicker) {
        // Try multiple selectors to find the active day button
        const selectedDayButton =
          dayPicker.querySelector('.active') ||
          dayPicker.querySelector('[class*="selected"]') ||
          dayPicker.querySelector('[aria-selected="true"]') ||
          dayPicker.querySelector('.highlighted') ||
          dayPicker.querySelector('.current-day') ||
          dayPicker.querySelector('[class*="active"]');

        if (selectedDayButton) {
          selectedDay = selectedDayButton.textContent?.trim();
        }
      }

      // Also get the selected time if any
      const timeInput = stationCalendar.querySelector('input[type="time"]');
      let selectedTime = null;
      if (timeInput) {
        selectedTime = (timeInput as HTMLInputElement).value;
      }

      // Find the calendar list component
      const calendarList = stationCalendar.querySelector(
        'app-search-station-detailed-result-routes-calendar-list'
      );

      if (calendarList) {
        // Keep only the entries for the current selected day
       

        // Remove any elements that are not part of the selected day
        const dayTabs = calendarList.querySelectorAll(
          '.day-selector, .day-tab, [role="tab"]'
        );
        if (dayTabs && dayTabs.length > 0) {
          dayTabs.forEach((tab) => {
            if (
              !tab.textContent?.includes(selectedDay) &&
              !tab.classList.contains('active') &&
              !tab.classList.contains('selected')
            ) {
              tab.remove();
            }
          });
        }

        // If we have day panels or sections, keep only the active one
        const dayPanels = calendarList.querySelectorAll(
          '.day-panel, .day-content, [role="tabpanel"]'
        );
        if (dayPanels && dayPanels.length > 1) {
          // Find active panel
          const activePanel = Array.from(dayPanels).find(
            (panel) =>
              panel.classList.contains('active') ||
              panel.getAttribute('aria-selected') === 'true' ||
              panel.classList.contains('selected')
          );

          if (activePanel) {
            // Keep only the active panel
            dayPanels.forEach((panel) => {
              if (panel !== activePanel) {
                panel.remove();
              }
            });
          } else if (selectedDay) {
            // If no active panel found but we have a selected day, try to match by content
            dayPanels.forEach((panel) => {
              if (!panel.textContent?.includes(selectedDay)) {
                panel.remove();
              }
            });
          }
        }

        // If there's a time filter, try to ensure we only show entries after the selected time
        if (selectedTime) {
          const timeElements = calendarList.querySelectorAll('.time');
          timeElements.forEach((timeEl) => {
            const entryTime = timeEl.textContent?.trim();
            if (entryTime && entryTime < selectedTime) {
              // Find the parent list item and remove it
              const listItem = timeEl.closest(
                'li, .list-item, mot-list-item, .mat-list-item'
              );
              if (listItem) {
                listItem.remove();
              }
            }
          });
        }
      }
    }

    // Preserve daily calendar table content
    const dailyCalendar = element.querySelector(
      'app-search-line-daily-calendar'
    );
    if (dailyCalendar) {
      // Ensure table height is set for printing
      const tableElement = dailyCalendar.querySelector('mot-table');
      if (tableElement) {
        tableElement.classList.add('print-visible');
        tableElement.setAttribute(
          'style',
          'display: block !important; margin-top: 1rem; min-height: auto !important; overflow: visible !important;'
        );

        // Make sure all rows are visible
        tableElement.querySelectorAll('tr').forEach((row) => {
          (row as HTMLElement).style.display = 'table-row';
          (row as HTMLElement).style.pageBreakInside = 'avoid';
        });

        // Make cells visible
        tableElement.querySelectorAll('td, th').forEach((cell) => {
          (cell as HTMLElement).style.display = 'table-cell';
          (cell as HTMLElement).style.padding = '8px';
        });
      }
    }

    // Ensure search-line-detailed-stops are visible
    const searchLineStops = element.querySelector(
      'app-search-line-detailed-stops'
    );
    if (searchLineStops) {

      // Make sure all stop items are visible
      searchLineStops.querySelectorAll('.flex').forEach((item) => {
        (item as HTMLElement).style.pageBreakInside = 'avoid';
        (item as HTMLElement).style.breakInside = 'avoid';

        // Ensure all text is visible
        item.querySelectorAll('.text-gray-300').forEach((el) => {
          (el as HTMLElement).classList.remove('text-gray-300');
          (el as HTMLElement).style.color = '#000000';
        });
      });

      // Make sure no items are trimmed or hidden
      searchLineStops
        .querySelectorAll('app-stop-indicator')
        .forEach((indicator) => {
          (indicator as HTMLElement).style.overflow = 'visible';
          (indicator as HTMLElement).style.display = 'block';
        });
    }

    // Preserve source-destination route content
    const sourceDestRoute = element.querySelector(
      'app-source-destination-detailed-result-route'
    );
    if (sourceDestRoute) {
      // Make sure all route sections and steps are visible
      sourceDestRoute
        .querySelectorAll('.route-section, .route-step, .section-item')
        .forEach((section) => {
          section.classList.add('print-visible');
          (section as HTMLElement).setAttribute(
            'style',
            'display: block; margin-bottom: 10px; page-break-inside: avoid;'
          );
        });
    }

    // Remove any tab headers
    element
      .querySelectorAll('.mat-mdc-tab-header, mat-tab-header, .mat-tab-header')
      .forEach((el) => {
        el.remove();
      });

    // Make sure only the active tab content is visible
    const tabBodies = element.querySelectorAll(
      '.mat-mdc-tab-body, .mat-tab-body'
    );

    // First check if there's an active tab and make sure it's visible
    const activeTabBody = element.querySelector(
      '.mat-mdc-tab-body-active, .mat-tab-body-active'
    );

    if (activeTabBody) {
      activeTabBody.classList.add('print-visible');
      (activeTabBody as HTMLElement).style.display = 'block';
      (activeTabBody as HTMLElement).style.visibility = 'visible';
      (activeTabBody as HTMLElement).style.overflow = 'visible';
      (activeTabBody as HTMLElement).style.height = 'auto';

      // Remove non-active tabs
      tabBodies.forEach((tabBody) => {
        if (
          !tabBody.classList.contains('mat-mdc-tab-body-active') &&
          !tabBody.classList.contains('mat-tab-body-active')
        ) {
          tabBody.remove();
        }
      });
    } else {
      // If no active tab is found, make the first tab visible
      if (tabBodies.length > 0) {
        const firstTabBody = tabBodies[0];
        firstTabBody.classList.add(
          'print-visible',
          'mat-mdc-tab-body-active',
          'mat-tab-body-active'
        );
        (firstTabBody as HTMLElement).style.display = 'block';
        (firstTabBody as HTMLElement).style.visibility = 'visible';
        (firstTabBody as HTMLElement).style.overflow = 'visible';
        (firstTabBody as HTMLElement).style.height = 'auto';

        // Remove other tabs
        for (let i = 1; i < tabBodies.length; i++) {
          tabBodies[i].remove();
        }
      }
    }

    // Hide empty containers that might cause extra space
    element
      .querySelectorAll('.empty-container, .no-print, .hidden-print')
      .forEach((el) => {
        el.remove();
      });

    // Clean up any empty divs that might have held interactive elements
    element.querySelectorAll('div:empty').forEach((el) => {
      if (el.children.length === 0 && !el.textContent.trim()) {
        el.remove();
      }
    });
  }

  private createMapContainer(mapImageUrl: string): HTMLElement {
    const mapContainer = document.createElement('div');
    mapContainer.style.marginTop = '20px';
    mapContainer.style.marginBottom = '20px';
    mapContainer.style.border = '1px solid #ccc';
    mapContainer.style.padding = '15px';
    mapContainer.style.boxShadow = '0 2px 5px rgba(0,0,0,0.1)';
    mapContainer.style.backgroundColor = '#f9f9f9';
    mapContainer.style.textAlign = 'center';
    mapContainer.style.pageBreakBefore = 'always'; // Force map to start on a new page
    mapContainer.classList.add('map-print-container');

    // Add heading
    const mapHeading = document.createElement('h3');
    mapHeading.style.marginBottom = '15px';
    mapHeading.style.fontSize = '18px';
    mapHeading.style.color = '#333';
    const currentLanguage = this.languageService.getSelectedLanguage().language;
    mapHeading.textContent = currentLanguage === 'he' ? 'מפה' : 'Map';
    mapContainer.appendChild(mapHeading);

    // Add map image with improved styling
    const mapImg = document.createElement('img');
    mapImg.src = mapImageUrl;
    mapImg.style.width = '100%';
    mapImg.style.height = 'auto';
    mapImg.style.maxWidth = '100%';
    mapImg.style.maxHeight = '800px';
    mapImg.style.objectFit = 'contain';
    mapImg.style.border = '1px solid #ddd';
    mapImg.style.borderRadius = '4px';
    mapImg.style.display = 'block';
    mapImg.style.margin = '0 auto';
    mapContainer.appendChild(mapImg);

    return mapContainer;
  }

  /**
   * Checks if the current device is a mobile device
   */
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Generates a PDF from the canvas and triggers download
   */
  private generateAndDownloadPDF(
    canvas: HTMLCanvasElement,
    filename: string
  ): void {
    const imgData = canvas.toDataURL('image/jpeg', 0.9);
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF('p', 'mm', 'a4', true);
    let position = 0;

    // Add image to PDF, handling pagination if image is taller than a page
    if (imgHeight <= pageHeight) {
      // Image fits on a single page
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
    } else {
      // Image needs multiple pages
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      position -= pageHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        position -= pageHeight;
      }
    }

    // Download the PDF
    pdf.save(filename);

    // Clean up canvas memory
    canvas.width = 0;
    canvas.height = 0;
  }

  private finalizeCapture(
    printContainer: HTMLElement,
    loadingIndicator: HTMLElement
  ): void {
    const finalizeStartTime = performance.now();
    
    html2canvas(printContainer, {
      allowTaint: true,
      useCORS: true,
      scale: this.CANVAS_SCALE, // Use optimized scale
      logging: false,
      foreignObjectRendering: false, // Disable for better performance
      proxy: undefined, // Disable proxy for better performance
      ignoreElements: (element) => {
        // Skip hidden elements to speed up rendering
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
          return true;
        }
        // Skip script tags, style tags, and other non-visible elements
        const tagName = element.tagName?.toLowerCase();
        if (['script', 'style', 'noscript', 'meta', 'link'].includes(tagName)) {
          return true;
        }
        // Skip elements with zero dimensions
        if (element instanceof HTMLElement) {
          const rect = element.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) {
            return true;
          }
        }
        return false;
      },
      onclone: (clonedDoc) => {
        const clonedContainer = clonedDoc.body.querySelector('div');
        if (clonedContainer) {
          clonedContainer.style.width = '100%';
          clonedContainer.style.backgroundColor = 'white';
        }
      },
    })
      .then((canvas) => {
        const canvasTime = performance.now() - finalizeStartTime;
        
        // Remove temporary elements
        document.body.removeChild(printContainer);
        this.removeLoadingIndicator();
        
        // Generate filename with date
        const date = new Date();
        const filename = `route-details-${date.getFullYear()}-${(
          date.getMonth() + 1
        )
          .toString()
          .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

        const pdfCreateStartTime = performance.now();
        // For mobile devices, directly download the PDF
        // For desktop, open the print window
        if (this.isMobileDevice()) {
          this.generateAndDownloadPDF(canvas, `${filename}.pdf`);
        } else {
          // Create a PDF and open print window
          const pdf = new jsPDF('p', 'mm', 'a4', true);
          const imgWidth = 210; // A4 width in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          const imgDataStartTime = performance.now();
          const imgData = canvas.toDataURL('image/jpeg', 0.9);

          pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

          const pdfBlob = pdf.output('blob');
          const objectUrl = URL.createObjectURL(pdfBlob);
          this.openPrintWindowForPDF(objectUrl, filename);
        }

        // Clean up canvas memory
        canvas.width = 0;
        canvas.height = 0;
        
        const totalProcessTime = performance.now() - this.processStartTime;
      })
      .catch((error) => {
        console.error('[PrintService] ❌ Error generating image for printing:', error);
        document.body.removeChild(printContainer);
        this.removeLoadingIndicator();
      });
  }

  private addMapPlaceholder(printContainer: HTMLElement): void {
    const mapPlaceholder = document.createElement('div');
    mapPlaceholder.style.marginTop = '30px';
    mapPlaceholder.style.marginBottom = '30px';
    mapPlaceholder.style.border = '1px solid #ccc';
    mapPlaceholder.style.padding = '15px';
    mapPlaceholder.style.textAlign = 'center';
    mapPlaceholder.style.minHeight = '200px';
    mapPlaceholder.style.display = 'flex';
    mapPlaceholder.style.flexDirection = 'column';
    mapPlaceholder.style.justifyContent = 'center';
    mapPlaceholder.style.backgroundColor = '#f9f9f9';

    const currentLanguage = this.languageService.getSelectedLanguage().language;
    const mapText =
      currentLanguage === 'he'
        ? 'המפה אינה זמינה להדפסה'
        : 'Map not available for printing';

    mapPlaceholder.innerText = mapText;
    printContainer.appendChild(mapPlaceholder);
  }

  /**
   * Checks if any tiles are expanded in the source-destination detailed result
   * @param contentElement The content element to check
   * @returns True if any tiles are expanded, false otherwise
   */
  private hasExpandedTiles(contentElement: HTMLElement): boolean {
    // Look for expanded panels in the source-destination detailed result
    const sourceDestDetailedResult = contentElement.querySelector(
      'app-source-destination-detailed-result-route'
    );

    if (sourceDestDetailedResult) {
      // Check for expanded mat-expansion-panel elements
      const expandedPanels = sourceDestDetailedResult.querySelectorAll(
        'mat-expansion-panel.mat-expanded, .mat-expansion-panel.mat-expanded'
      );

      if (expandedPanels.length > 0) {
        return true;
      }

      // Check for expanded content in route sections
      const routeSections = sourceDestDetailedResult.querySelectorAll(
        'app-detailed-result-route-pedestrian, app-detailed-result-route-transit, app-detailed-result-route-vehicle, app-detailed-result-route-destination'
      );

      for (const section of Array.from(routeSections)) {
        // Check if the section has expanded content (e.g., stations list)
        const expandedContent = section.querySelector(
          '.expanded-content, .stations-list, .stops-list'
        );
        if (expandedContent && expandedContent.clientHeight > 0) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Expands all tiles in the source-destination detailed result
   * @param contentElement The content element to modify
   */
  private expandAllTiles(contentElement: HTMLElement): void {
    // Find the source-destination detailed result
    const sourceDestDetailedResult = contentElement.querySelector(
      'app-source-destination-detailed-result-route'
    );

    if (sourceDestDetailedResult) {

      // 1. Expand all mat-expansion-panel elements
      const expansionPanels = sourceDestDetailedResult.querySelectorAll(
        'mat-expansion-panel, .mat-expansion-panel'
      );

      expansionPanels.forEach((panel) => {
        // Add the expanded class
        panel.classList.add('mat-expanded');

        // Set the expanded attribute
        panel.setAttribute('aria-expanded', 'true');

        // Find and show the content
        const content = panel.querySelector('.mat-expansion-panel-content');
        if (content) {
          content.classList.add('mat-expansion-panel-content-show');
          content.setAttribute('style', 'visibility: visible; height: auto;');
        }
      });

      // 2. Expand all route sections that might have collapsible content
      const routeSections = sourceDestDetailedResult.querySelectorAll(
        'app-detailed-result-route-pedestrian, app-detailed-result-route-transit, app-detailed-result-route-vehicle, app-detailed-result-route-destination'
      );

      routeSections.forEach((section) => {
        // Find and expand any collapsible content
        const collapsibleContent = section.querySelectorAll(
          '.collapsible-content, .stations-list, .stops-list, .expanded-content'
        );

        collapsibleContent.forEach((content) => {
          content.classList.add('expanded');
          content.setAttribute(
            'style',
            'display: block; visibility: visible; height: auto;'
          );
        });

        // Find and expand any hidden elements that should be visible
        const hiddenElements = section.querySelectorAll(
          '.hidden, [hidden], [style*="display: none"], [style*="visibility: hidden"]'
        );

        hiddenElements.forEach((element) => {
          element.classList.remove('hidden');
          element.removeAttribute('hidden');
          element.setAttribute('style', 'display: block; visibility: visible;');
        });
      });

      // 3. Make sure all content is visible for printing
      const allContent = sourceDestDetailedResult.querySelectorAll('*');
      allContent.forEach((element) => {
        // Remove any styles that might hide content
        const style = element.getAttribute('style');
        if (
          style &&
          (style.includes('display: none') ||
            style.includes('visibility: hidden'))
        ) {
          element.setAttribute(
            'style',
            style
              .replace(/display:\s*none/g, 'display: block')
              .replace(/visibility:\s*hidden/g, 'visibility: visible')
          );
        }
      });
    }
  }

  /**
   * Optimize container DOM before capture to improve performance
   * Removes unnecessary elements and simplifies structure
   */
  private optimizeContainerForCapture(container: HTMLElement): void {
    // Remove script tags
    container.querySelectorAll('script').forEach(el => el.remove());
    
    // Remove style tags (we have our own print styles)
    container.querySelectorAll('style').forEach(el => {
      // Keep only print-related styles
      if (!el.textContent?.includes('@media print')) {
        el.remove();
      }
    });
    
    // Remove noscript tags
    container.querySelectorAll('noscript').forEach(el => el.remove());
    
    // Remove meta tags
    container.querySelectorAll('meta').forEach(el => el.remove());
    
    // Remove link tags (except stylesheets if needed)
    container.querySelectorAll('link[rel="stylesheet"]').forEach(el => {
      // Keep only if it's critical for print
      if (!el.getAttribute('media')?.includes('print')) {
        el.remove();
      }
    });
    
    // Remove elements with display: none (more aggressive)
    const allElements = container.querySelectorAll('*');
    allElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') {
          el.remove();
        }
      }
    });
  }

  /**
   * Remove unnecessary elements from cloned document
   */
  private removeUnnecessaryElements(element: HTMLElement): void {
    // Remove script tags
    element.querySelectorAll('script').forEach(el => el.remove());
    
    // Remove style tags
    element.querySelectorAll('style').forEach(el => el.remove());
    
    // Remove noscript
    element.querySelectorAll('noscript').forEach(el => el.remove());
    
    // Remove meta and link tags
    element.querySelectorAll('meta, link').forEach(el => el.remove());
  }
}
